import * as React from "react"
import { ZoneType } from "@/types"
import { useQueries } from "@tanstack/react-query"

import { getBooksZone, getZoneDropdown } from "@/lib/api/billing"
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu"

import { Input } from "../ui/input"

type ZoneBookType = {
  id: number
}

type DropdownType = {
  id: number
  label: string
  value: string
  zone_id?: number
}

type BooksDropdownType = {
  id: number
  name: string
  zone_id: number
  zone: string
  description: string
}

export function MapFilter({
  setBooks,
  zones,
  books,
  setZones = () => {},
}: {
  setBooks: React.Dispatch<React.SetStateAction<number[]>>
  zones: number[]
  books: number[]
  setZones?: React.Dispatch<React.SetStateAction<number[]>>
}) {
  const queryConfigs = [
    {
      queryKey: ["zones"],
      queryFn: getZoneDropdown,
    },
    ...zones.map((zoneId) => ({
      queryKey: ["booksZone", zoneId],
      queryFn: () => getBooksZone(zoneId),
    })),
  ]

  const results = useQueries({
    queries: queryConfigs,
  })

  // Zones and books data
  const zonesData = results[0]?.data || []
  const booksData = results.slice(1).map((res) => res.data || [])

  // Group books by zone_id
  const booksByZone = booksData.reduce(
    (acc, books) => {
      books.forEach((book: BooksDropdownType) => {
        if (!acc[book.zone_id]) acc[book.zone_id] = []
        acc[book.zone_id].push(book)
      })
      return acc
    },
    {} as Record<number, BooksDropdownType[]>,
  )

  const handleZoneCheckboxChange = (id: number) => {
    setZones((prev) =>
      prev.includes(id)
        ? prev.filter((zoneId) => zoneId !== id)
        : [...prev, id],
    )
  }

  //Individual book selection
  const handleBookSelect = (bookId: number) => {
    setBooks((prev) =>
      prev.includes(bookId)
        ? prev.filter((id) => id !== bookId)
        : [...prev, bookId],
    )
  }

  return (
    <>
      <span className="font-semibold">Zone</span>
      <DropdownMenuSeparator className="px-2" />
      <div className="my-1 flex flex-col gap-2">
        {zonesData.map((zone: DropdownType) => {
          return (
            <div key={zone.id} className="flex flex-col">
              <label className="flex items-center space-x-2">
                <Input
                  type="checkbox"
                  value={zone.id}
                  checked={zones.includes(zone.id)}
                  onChange={() => handleZoneCheckboxChange(zone.id)}
                  className="h-4 w-4"
                />
                <span onClick={() => handleZoneCheckboxChange(zone.id)}>
                  {zone.label}
                </span>
              </label>

              {/* Display books for the selected zone */}
              {zones.includes(zone.id) && booksByZone[zone.id]?.length > 0 && (
                <div className="ml-6 flex flex-col gap-2">
                  {booksByZone[zone.id].map((book: DropdownType) => {
                    return (
                      <label
                        key={book.id}
                        className="flex items-center space-x-2"
                      >
                        <Input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={books.includes(book.id)}
                          onChange={() => handleBookSelect(book.id)}
                        />
                        <span>{book.label}</span>
                      </label>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </>
  )
}
